import { DeviceReport, UnassignedDeviceReport } from "@/data/models/DeviceReport";

/**
 * What the Testberichte filter screen asks for, and the pure logic that applies it.
 *
 * Split the way the RENO review queue splits its filters: the parts Firestore can answer become a
 * query, and everything else narrows the loaded page here. That division is deliberate rather than
 * lazy — each additional queryable field would need its own composite index with collection-group
 * scope, and device reports are one document per device, so a page is a cheap thing to narrow.
 *
 * The one query-level filter is the period, because it is a range and a range cannot be applied
 * afterwards without loading everything.
 */

/** Which reports to show, by whether they are attached to a device yet. */
export enum ReportAssignment {
  ALL = "all",
  ASSIGNED = "assigned",
  UNASSIGNED = "unassigned",
}

/** How the period is bounded. Mutually exclusive, as in the RENO queue. */
export enum ReportPeriodMode {
  NONE = "none",
  AGE = "age",
  FROM = "from",
}

/** Pass, fail, or don't care. */
export enum ReportOutcome {
  ALL = "all",
  PASSED = "passed",
  FAILED = "failed",
}

export interface DeviceReportFilters {
  /** Exact serial. When set, everything else still applies but the query is per device. */
  serialNo: string;
  /** `deviceCategory` values to keep. Empty means every category. */
  deviceCategories: string[];
  /** Report types to keep. Empty means every type. */
  reportTypes: string[];
  periodMode: ReportPeriodMode;
  /** Only meaningful for `AGE`. */
  maxAgeMonths: number;
  /** Only meaningful for `FROM`, as `YYYY-MM-DD`. */
  fromDate: string;
  outcome: ReportOutcome;
  assignment: ReportAssignment;
}

/** The filter set a freshly opened screen starts from: everything, newest first. */
export function emptyDeviceReportFilters(): DeviceReportFilters {
  return {
    serialNo: "",
    deviceCategories: [],
    reportTypes: [],
    periodMode: ReportPeriodMode.NONE,
    maxAgeMonths: 12,
    fromDate: "",
    outcome: ReportOutcome.ALL,
    assignment: ReportAssignment.ALL,
  };
}

/**
 * The earliest report date the filters allow, or `null` for no lower bound.
 *
 * @param now Injected so the age window is testable.
 */
export function getPeriodStart(filters: DeviceReportFilters, now: Date): Date | null {
  if (filters.periodMode === ReportPeriodMode.AGE) {
    const start = new Date(now.getTime());

    start.setMonth(start.getMonth() - filters.maxAgeMonths);

    return start;
  }

  if (filters.periodMode === ReportPeriodMode.FROM && filters.fromDate) {
    const start = new Date(`${filters.fromDate}T00:00:00`);

    return Number.isNaN(start.getTime()) ? null : start;
  }

  return null;
}

/** Whether an outcome string counts as a pass. Anything else is a failure. */
function isPass(outcome: string | null | undefined): boolean {
  return (outcome ?? "").toLowerCase() === "pass";
}

/**
 * Whether a value passes an "empty means everything" list filter.
 *
 * A report whose value is missing is kept only when the filter is unset, so narrowing by category
 * never silently includes reports that cannot state one.
 */
function matchesList(selected: string[], value: string | null | undefined): boolean {
  if (selected.length === 0) {
    return true;
  }

  return value !== null && value !== undefined && selected.includes(value);
}

function matchesOutcome(outcome: ReportOutcome, value: string | null | undefined): boolean {
  if (outcome === ReportOutcome.ALL) {
    return true;
  }

  // A report with no result at all is neither passed nor failed, so it is excluded by both — being
  // asked for failures and shown a report with no verdict would misrepresent it.
  if (!value) {
    return false;
  }

  return outcome === ReportOutcome.PASSED ? isPass(value) : !isPass(value);
}

function matchesPeriod(start: Date | null, reportedAt: Date | null): boolean {
  if (!start) {
    return true;
  }

  // No readable timestamp means it cannot be placed in a period, so a bounded period excludes it.
  return reportedAt !== null && reportedAt.getTime() >= start.getTime();
}

function matchesSerial(serialNo: string, value: string | null | undefined): boolean {
  return serialNo === "" || (value ?? "").toUpperCase() === serialNo.toUpperCase();
}

/**
 * Whether a report already attached to a device passes the filters.
 *
 * The period is normally applied by the query; it is re-checked here so the same predicate holds
 * for rows that arrived another way (a per-device query, which has no range on it).
 */
export function matchesAssignedReport(
  report: DeviceReport,
  filters: DeviceReportFilters,
  now: Date
): boolean {
  if (filters.assignment === ReportAssignment.UNASSIGNED) {
    return false;
  }

  return (
    matchesSerial(filters.serialNo, report.serialNo) &&
    matchesList(filters.deviceCategories, report.deviceCategory) &&
    matchesList(filters.reportTypes, report.reportType) &&
    matchesOutcome(filters.outcome, report.overallResult) &&
    matchesPeriod(getPeriodStart(filters, now), report.reportedAt?.toDate() ?? null)
  );
}

/**
 * Whether a report still waiting in the unassigned area passes the filters.
 *
 * It carries less: no `deviceCategory`, and a serial only when the upload had one. A category
 * filter therefore excludes every unassigned report rather than guessing a category from a serial
 * that may not exist — the screen says so in its own words rather than silently dropping them.
 */
export function matchesUnassignedReport(
  report: UnassignedDeviceReport,
  filters: DeviceReportFilters,
  now: Date
): boolean {
  if (filters.assignment === ReportAssignment.ASSIGNED) {
    return false;
  }

  if (filters.deviceCategories.length > 0) {
    return false;
  }

  return (
    matchesSerial(filters.serialNo, report.serialNo) &&
    matchesList(filters.reportTypes, report.reportType) &&
    matchesOutcome(filters.outcome, report.overallResult) &&
    matchesPeriod(getPeriodStart(filters, now), report.reportedAt)
  );
}

/** Whether the unassigned area needs to be read at all for this filter set. */
export function needsUnassignedReports(filters: DeviceReportFilters): boolean {
  return (
    filters.assignment !== ReportAssignment.ASSIGNED && filters.deviceCategories.length === 0
  );
}

/** Whether the indexed reports need to be read at all for this filter set. */
export function needsAssignedReports(filters: DeviceReportFilters): boolean {
  return filters.assignment !== ReportAssignment.UNASSIGNED;
}

/**
 * Filters as URL query parameters, so the results screen is reachable by link and survives a
 * refresh. Only non-default values are written, which keeps a shared link readable.
 */
export function toQueryParams(filters: DeviceReportFilters): Record<string, string> {
  const defaults = emptyDeviceReportFilters();
  const params: Record<string, string> = {};

  if (filters.serialNo) params.serialNo = filters.serialNo;
  if (filters.deviceCategories.length) params.categories = filters.deviceCategories.join(",");
  if (filters.reportTypes.length) params.types = filters.reportTypes.join(",");
  if (filters.outcome !== defaults.outcome) params.outcome = filters.outcome;
  if (filters.assignment !== defaults.assignment) params.assignment = filters.assignment;

  if (filters.periodMode === ReportPeriodMode.AGE) {
    params.months = String(filters.maxAgeMonths);
  } else if (filters.periodMode === ReportPeriodMode.FROM && filters.fromDate) {
    params.from = filters.fromDate;
  }

  return params;
}

/**
 * Filters from URL query parameters.
 *
 * Every unknown or malformed value falls back to its default rather than throwing: a hand-edited
 * link should show a sane screen, not an error.
 */
export function fromQueryParams(query: Record<string, unknown>): DeviceReportFilters {
  const filters = emptyDeviceReportFilters();
  const text = (key: string): string =>
    typeof query[key] === "string" ? (query[key] as string) : "";

  filters.serialNo = text("serialNo").trim().toUpperCase();
  filters.deviceCategories = text("categories").split(",").filter(Boolean);
  filters.reportTypes = text("types").split(",").filter(Boolean);

  const outcome = text("outcome");
  if ((Object.values(ReportOutcome) as string[]).includes(outcome)) {
    filters.outcome = outcome as ReportOutcome;
  }

  const assignment = text("assignment");
  if ((Object.values(ReportAssignment) as string[]).includes(assignment)) {
    filters.assignment = assignment as ReportAssignment;
  }

  const months = Number(text("months"));
  if (Number.isFinite(months) && months > 0) {
    filters.periodMode = ReportPeriodMode.AGE;
    filters.maxAgeMonths = months;
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(text("from"))) {
    filters.periodMode = ReportPeriodMode.FROM;
    filters.fromDate = text("from");
  }

  return filters;
}
